import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, LogOut, Shield, Users2, Settings as SettingsIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useSiteConfig, fileUrl } from "@/lib/siteConfig";

const baseNav = [
  { to: "/agent", label: "Overview", icon: LayoutDashboard, end: true, testId: "nav-overview" },
  { to: "/agent/clients", label: "Clients", icon: Users, end: false, testId: "nav-clients" },
];

const adminNav = [
  { to: "/agent/agents", label: "Team", icon: Users2, end: false, testId: "nav-team", admin: true },
  { to: "/agent/settings", label: "Settings", icon: SettingsIcon, end: false, testId: "nav-settings", admin: true },
];

export default function AgentLayout() {
  const navigate = useNavigate();
  const { agent, logout } = useAuth();
  const { config } = useSiteConfig();
  const logoUrl = fileUrl(config.logo_file_id);
  const navItems = agent?.role === "admin" ? [...baseNav, ...adminNav] : baseNav;

  const handleLogout = () => {
    logout();
    navigate("/agent/login");
  };

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Sidebar */}
      <aside className="w-60 bg-bone border-r border-line h-screen sticky top-0 flex flex-col">
        <div className="h-16 px-5 flex items-center border-b border-line">
          <Link to="/agent" className="flex items-center gap-2" data-testid="sidebar-brand">
            {logoUrl ? (
              <img src={logoUrl} alt={config.brand_name} className="h-7 w-auto" />
            ) : (
              <>
                <div className="w-8 h-8 bg-forest rounded-sm flex items-center justify-center">
                  <Shield className="w-4 h-4 text-bone" strokeWidth={2} />
                </div>
                <span className="font-heading font-medium text-lg">{config.brand_name}</span>
              </>
            )}
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-forest text-bone"
                    : "text-sage hover:bg-stone hover:text-forest"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.7} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-line">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-sage uppercase tracking-[0.15em] flex items-center gap-2">
              {agent?.role === "admin" && <Shield className="w-3 h-3 text-clay" />}
              Signed in
            </div>
            <div className="text-sm font-medium text-forest truncate" data-testid="sidebar-agent-name">
              {agent?.name}
            </div>
            <div className="text-xs text-sage truncate">{agent?.email}</div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="w-full border-line text-sage hover:text-danger hover:border-danger rounded-sm justify-start"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
